import React, { useEffect, useMemo } from 'react';
import DefaultReactTable, { multiSelectFilterColumn } from '../../utils/react-table';
import { connect } from 'react-redux';
import { selectors as userSelectors } from '../../state/modules/users';
import { selectors as groupSelectors } from '../../state/modules/groups';
import { selectors as skillSelectors } from '../../state/modules/skills';
import { selectors as rankSelectors } from '../../state/modules/ranks';
import { operations as skillOperations } from '../../state/modules/skills';
import { operations as incidentOperations } from '../../state/modules/incidents';
import { operations as membershipOperations } from '../../state/modules/memberships';
import { selectors as membershipSelectors } from '../../state/modules/memberships';
import { registerForIncidentUpdates } from '../../state/modules/incidents/operations';
import { selectors as incidentSelectors } from '../../state/modules/incidents';
import { operations as standbyDutyFunctionOperations } from '../../state/modules/standby-duty-functions';
import { selectors as standbyDutyFunctionSelectors } from '../../state/modules/standby-duty-functions';
import { operations as rankOperations } from '../../state/modules/ranks';
import Loader from '../general/Loader';
import DashboardAPIManager from '../dashboards/DashboardAPIManager';
import UserContact from '../general/UserContact';
import AvailabilityIcon from '../general/AvailabilityIcon';
import { formatTimeOnly } from '../../utils/time';
import SkillIconList from '../general/SkillIconList';
import GroupName from '../general/GroupName';
import { IncidentResponderStatusIcon } from '../general/IncidentResponderStatusIcon';
import { PulseLoader } from 'react-spinners';
import { compact, isEmpty, sortBy } from 'lodash';
import { ReportedStatus, PerformedRank, AttendanceTimes } from '../../utils/incidents/decoratorTable';
import ErrorBoundary from '../general/ErrorBoundary';
import './IncidentUserTable.css';

const LARGE_COL = 240;
const MEDIUM_COL = 140;
const SMALL_COL = 100;

const ColumnLoader = () => <PulseLoader height="2px" width="100%" color="#C30000" />;

const sortDateTime = (currentValue, valueToCompare, desc) => {
  // if both values are falsey, use index as tie breaker
  if (!currentValue && !valueToCompare) return 0;
  // we wish to keep empty values at the bottom regardless of asc or desc.
  // To achieve this, we need to redefine what it means to be empty.
  // We force empty to the bottom regardless of direction with the following logic:
  // When ascending (positive means down in the list):
  // - an empty currentValue should return positive regardless of the value to compare
  // - an empty valueToCompare should return negative regardless of the current value
  // When descending (positive means up in the list):
  // - an empty valueToCompare should return positive regardless of the current value
  // - an empty currentValue should return negative regardless of the value to compare
  const positiveEmptyControl = desc ? valueToCompare : currentValue;
  const negativeEmptyControl = desc ? currentValue : valueToCompare;

  if (!positiveEmptyControl || currentValue > valueToCompare) return 1;
  if (!negativeEmptyControl || currentValue < valueToCompare) return -1;

  return 0;
};

const availabilityFilterOptions = {
  exception_available: { i18n_key: 'availability_status.exception.available' },
  exception_unavailable: { i18n_key: 'availability_status.exception.unavailable' },
  standby_duty_available: { i18n_key: 'availability_status.standby_duty.available' }
};

const incidentResponderStatusFilterOptions = ['acknowledged', 'pending', 'rejected', 'expired'];

const reportedStatusFilterOptions = ['dispatched', 'unknown', 'shown_up', 'no_show'];

const IncidentUsersTable = ({
  incidentId,
  groupId,
  incidents,
  memberships,
  skills,
  ranks,
  groups,
  functions,
  groupScope,
  incidentsLoaded,
  usersLoaded,
  groupsLoaded,
  skillsLoaded,
  ranksLoaded,
  membershipsLoaded,
  fetchIncidents,
  fetchSkills,
  fetchRanks,
  fetchMemberships,
  registerForIncidentUpdates
}) => {
  // For perceived performance, we first fetch the incident data
  // only, this is to ensure the table is displayed as soon as
  // we have some data to show
  useEffect(() => {
    // Fetch the incident initial data
    fetchIncidents({ ids: [incidentId] });
    registerForIncidentUpdates(groupId);
  }, []);

  // Once the incident data has been loaded, we dispatch all the
  // secondary fetches
  useEffect(() => {
    // Fetch all the secondary data
    const apiManager = new DashboardAPIManager();
    const responses = incidents[incidentId]?.incident_responses || [];
    const groupIds = groupScope.join(',');

    if (incidentsLoaded) {
      apiManager.fetchUsers({
        ids: responses.map(r => r.user_id),
        params: { groupIds, presentation_type: 'basic' },
        onlyMissing: true
      });
    }

    apiManager.fetchGroups({
      ids: groupIds,
      params: {
        presentation_type: 'basic',
        ancestor_group_id: groupId,
        in_hierarchy: true
      }
    });

    apiManager.fetchStandbyDutyFunctions({
      ids: responses.flatMap(r => r.active_duty_function_ids)
    });

    fetchMemberships({ params: { group_ids: groupScope.join(',') } });

    fetchSkills({
      params: { group_ids: groupScope.join(','), in_hierarchy: true }
    });

    fetchRanks({
      ids: responses.flatMap(r => [r.performed_rank_id, r.original_rank_id]),
      onlyMissing: true
    });
  }, [incidentsLoaded]);

  const responses = incidents[incidentId]?.incident_responses || [];

  const columns = useMemo(() => {
    const loadingStatuses = {
      usersLoaded: usersLoaded,
      groupsLoaded: groupsLoaded,
      skillsLoaded: skillsLoaded,
      ranksLoaded: ranksLoaded
    };

    const showAssignedSkills = responses.some(r => r.assigned_skill_ids.length > 0);
    const showRanks = responses.some(
      // show the column if any of the responders was dispatched and has either a rank
      // or a performed rank
      r => r.reported_status === 'dispatched' && (!!r.original_rank_id || !!r.performed_rank_id)
    );
    const showAttendanceTimes = responses.some(r => r.reported_status === 'dispatched' && !!r.end_time);

    return compact([
      multiSelectFilterColumn({
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.available_at_incident_creation')}`,
        accessor: d => {
          if (d.on_duty) return availabilityFilterOptions['standby_duty_available'].i18n_key;
          return availabilityFilterOptions[
            d.available_at_incident_creation ? 'exception_available' : 'exception_unavailable'
          ].i18n_key;
        },
        width: 120,
        id: 'available_at_incident_creation',
        selectOptions: Object.values(availabilityFilterOptions).map(op => ({
          value: op.i18n_key,
          html: I18n.t(op.i18n_key)
        })),
        Cell: row => (
          <div>
            <AvailabilityIcon
              available={row.original.available_at_incident_creation}
              standbyDuty={row.original.on_duty}
            />{' '}
            <SkillIconList
              skills={row.original.active_duty_function_ids.map(f => functions[f])}
              style={{ marginLeft: '6px' }}
            />
          </div>
        )
      }),
      {
        Header: `${I18n.t('name')}`,
        accessor: d => d.user_name,
        id: 'name',
        width: LARGE_COL,
        Cell: row => {
          const membership = memberships[row.original.membership_id];
          const memberSkills = sortBy(
            (membership?.skills || []).map(id => skills[id]),
            'order'
          );

          return (
            <>
              <UserContact
                userId={row.original.user_id}
                groupAvailabilityContextId={row.original.group_id}
                displayFullName={true}
              />{' '}
              {!isEmpty(memberSkills) && <SkillIconList skills={memberSkills} />}
            </>
          );
        }
      },
      multiSelectFilterColumn({
        Header: `${I18n.t('attributes.status')}`,
        accessor: d => d.status,
        id: 'status',
        selectOptions: Object.values(incidentResponderStatusFilterOptions).map(op => ({
          value: op,
          html: I18n.t(`classy_enum.incidents/incident_response_status.${op}`)
        })),
        sortType: 'basic',
        width: MEDIUM_COL,
        Cell: row => <IncidentResponderStatusIcon status={row.original.status} />
      }),
      {
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.alerted_at')}`,
        id: 'alerted_at',
        accessor: d => d.alerted_at,
        filterable: false,
        width: MEDIUM_COL,
        sortMethod: sortDateTime,
        Cell: row => formatTimeOnly(row.original.alerted_at)
      },
      {
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.delivery_latency')}`,
        id: 'delivery_latency',
        accessor: d => d.delivery_latency,
        filterable: false,
        width: MEDIUM_COL,
        Cell: d => (
          <div className="text-end">
            {d.original.delivery_latency ? `${d.original.delivery_latency?.toFixed(1)}s` : null}
          </div>
        )
      },
      {
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.responded_at')}`,
        id: 'responded_at',
        accessor: d => d.responded_at,
        filterable: false,
        width: MEDIUM_COL,
        sortMethod: sortDateTime,
        Cell: row => formatTimeOnly(row.original.responded_at)
      },
      multiSelectFilterColumn({
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.reported_status')}`,
        accessor: d => d.reported_status,
        id: 'reported_status',
        selectOptions: reportedStatusFilterOptions.map(op => ({
          value: op,
          html: I18n.t(`classy_enum.incidents/incident_response_reported_status.${op}`)
        })),
        width: SMALL_COL,
        Cell: row => <ReportedStatus status={row.original.reported_status} />
      }),
      showRanks && {
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.performed_rank')}`,
        accessor: d => {
          const rank = ranks[d.performed_rank_id];
          return rank?.name;
        },
        id: 'performed_rank',
        width: LARGE_COL,
        Cell: row => {
          if (!loadingStatuses.ranksLoaded) return <ColumnLoader />;

          if (!ranks[row.original.performed_rank_id]) return null;

          return (
            <PerformedRank
              reported_status={row.original.reported_status}
              performed_rank={ranks[row.original.performed_rank_id]}
              original_rank={ranks[row.original.original_rank_id]}
            />
          );
        }
      },
      showAssignedSkills && {
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.assigned_skills')}`,
        accessor: d => {
          const skill = skills[d.assigned_skill_ids];
          return skill?.name;
        },
        id: 'assigned_skills',
        width: MEDIUM_COL,
        Cell: row => {
          if (!loadingStatuses.skillsLoaded) return <ColumnLoader />;
          return <SkillIconList skills={row.original.assigned_skill_ids.map(id => skills[id])} />;
        }
      },
      showAttendanceTimes && {
        Header: `${I18n.t('activerecord.attributes.incidents/incident_response.attendance_times')}`,
        id: 'attendance_times',
        filterable: false,
        width: LARGE_COL,
        Cell: row => {
          // Attendance times defaults to the incident start and end time when not present.
          // However, if the status is not dispatched, we should not show this value at all.
          // Only dispatched / attendees should have attendance times visible
          if (row.original.reported_status !== 'dispatched') return null;
          return <AttendanceTimes start={row.original.start_time} end={row.original.end_time} />;
        }
      },
      {
        Header: `${I18n.t('activerecord.models.group')}`,
        accessor: d => {
          const group = groups[d.group_id];
          return group?.name;
        },
        id: 'groups',
        Cell: row => {
          if (!loadingStatuses.groupsLoaded) return <ColumnLoader />;

          const group = groups[row.original.group_id];
          if (!group) return null;
          return <GroupName group={group} fullName={true} />;
        }
      }
    ]);
  }, [responses, groupsLoaded, usersLoaded, membershipsLoaded, skillsLoaded, ranksLoaded]);

  // For percieved performance purposes, we render the table
  // as soon as the incident data is loaded. The individual
  // columns will handle it's rendering once their column specific
  // data has been loaded
  if (!incidentsLoaded) return <Loader />;

  return (
    <ErrorBoundary>
      <DefaultReactTable
        filterable
        columns={columns}
        data={responses}
        defaultSorted={[{ id: 'status', desc: false }]}
      />
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  usersLoaded: userSelectors.initialLoadComplete(state),
  groupsLoaded: groupSelectors.initialLoadComplete(state),
  incidentsLoaded: incidentSelectors.initialLoadComplete(state),
  ranksLoaded: rankSelectors.initialLoadComplete(state),
  skillsLoaded: skillSelectors.initialLoadComplete(state),
  membershipsLoaded: membershipSelectors.initialLoadComplete(state),
  ranks: rankSelectors.byId(state),
  incidents: state.incidents.byId,
  skills: skillSelectors.byId(state),
  memberships: membershipSelectors.byId(state),
  functions: standbyDutyFunctionSelectors.byId(state),
  groups: groupSelectors.byId(state)
});

const mapDispatchToProps = {
  fetchRanks: rankOperations.fetch,
  fetchIncidents: incidentOperations.fetch,
  fetchSkills: skillOperations.fetch,
  fetchMemberships: membershipOperations.fetch,
  fetchStandbyDutyFunctions: standbyDutyFunctionOperations.fetch,
  registerForIncidentUpdates: registerForIncidentUpdates
};

export default connect(mapStateToProps, mapDispatchToProps)(IncidentUsersTable);
